// ---------------------------------------------------------------------------
// DataTable Renderer
// ---------------------------------------------------------------------------
// Engagement primitive — publishes the editor `DataTable` block as a
// semantic `<table>` with a `<thead>` of comma-separated header labels
// and a `<tbody>` of comma-separated row cells (one row per textarea
// line). Empty cells render as an empty `<td>`. Rows wider than the
// header count are truncated; rows narrower are padded with empty cells
// so each row keeps a stable column count for screen readers.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { text } from "../escape.js";

interface DataTableProps {
  headers?: string;
  rows?: string;
}

function parseHeaders(value: unknown): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function parseRows(value: unknown, columnCount: number): string[][] {
  if (typeof value !== "string" || columnCount === 0) return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const cells = line.split(",").map((cell) => cell.trim());
      if (cells.length >= columnCount) {
        return cells.slice(0, columnCount);
      }
      const padded = cells.slice();
      while (padded.length < columnCount) {
        padded.push("");
      }
      return padded;
    });
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as DataTableProps;

  const headers = parseHeaders(p.headers);
  if (headers.length === 0) {
    return "<!-- data-table missing headers -->";
  }

  const rows = parseRows(p.rows, headers.length);
  if (rows.length === 0) {
    return "<!-- data-table missing rows -->";
  }

  const headHtml = headers
    .map((label) => `<th scope="col" class="ease-data-table__th">${text(label)}</th>`)
    .join("");

  const bodyHtml = rows
    .map((row) => {
      const cells = row
        .map((cell) => `<td class="ease-data-table__td">${text(cell)}</td>`)
        .join("");
      return `      <tr class="ease-data-table__row">${cells}</tr>`;
    })
    .join("\n");

  return `<section class="ease-section ease-data-table">
  <table class="ease-data-table__table">
    <thead><tr>${headHtml}</tr></thead>
    <tbody>
${bodyHtml}
    </tbody>
  </table>
</section>`;
}

registerRenderer("data-table", { render });
